import { auth, db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Metadata } from '../types/common';

/**
 * Error logging is ON by default and can be switched off with
 * REACT_APP_DISABLE_ERROR_LOGGING=true.
 *
 * It used to be gated on REACT_APP_ERROR_LOGGING === 'true', a variable that
 * was not set anywhere in the repo, so production errors were written nowhere
 * and printed nowhere. An opt-out default means a missing variable can never
 * silence the pipeline again.
 */
const ERROR_LOGGING_ENABLED = process.env.REACT_APP_DISABLE_ERROR_LOGGING !== 'true';
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development';

/** Offline queue bound - errors are dropped oldest-first past this. */
const MAX_QUEUED_ERRORS = 50;

/** Field names whose values must never reach a log. */
const SENSITIVE_FIELD_PATTERN =
  /pass(word|phrase)?|secret|token|api[-_]?key|auth|credential|ssn|social.?security|card(number)?|cvv|cvc|pin|routing|account.?number/i;

export interface ErrorLog {
  message: string;
  stack?: string;
  componentStack?: string;
  userAgent: string;
  url: string;
  timestamp: Date;
  userId?: string;
  userEmail?: string;
  errorType: 'javascript' | 'network' | 'validation' | 'authentication' | 'permission';
  severity: 'low' | 'medium' | 'high' | 'critical';
  metadata?: Metadata;
}

export interface NetworkError {
  url: string;
  method: string;
  status: number;
  statusText: string;
  response?: unknown;
}

class ErrorService {
  private static instance: ErrorService;
  private errorQueue: ErrorLog[] = [];
  private isOnline: boolean = navigator.onLine;

  private constructor() {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrorQueue();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService();
    }
    return ErrorService.instance;
  }

  /**
   * Log a JavaScript error
   */
  async logError(
    error: Error,
    errorInfo?: { componentStack?: string },
    severity: ErrorLog['severity'] = 'medium',
    metadata?: Metadata
  ): Promise<void> {
    const errorLog: ErrorLog = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
      errorType: 'javascript',
      severity,
      metadata
    };

    // Add user info if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      errorLog.userId = currentUser.id;
      errorLog.userEmail = currentUser.email;
    }

    await this.saveError(errorLog);
  }

  /**
   * Log a network error
   */
  async logNetworkError(
    error: NetworkError,
    severity: ErrorLog['severity'] = 'medium'
  ): Promise<void> {
    const errorLog: ErrorLog = {
      message: `Network error: ${error.method} ${error.url} - ${error.status} ${error.statusText}`,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
      errorType: 'network',
      severity,
      metadata: {
        requestUrl: error.url,
        requestMethod: error.method,
        responseStatus: error.status,
        responseStatusText: error.statusText,
        responseData: error.response as string | number | boolean | null
      }
    };

    // Add user info if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      errorLog.userId = currentUser.id;
      errorLog.userEmail = currentUser.email;
    }

    await this.saveError(errorLog);
  }

  /**
   * Log a validation error
   */
  async logValidationError(
    field: string,
    value: unknown,
    errorMessage: string,
    metadata?: Metadata
  ): Promise<void> {
    const errorLog: ErrorLog = {
      message: `Validation error on field '${field}': ${errorMessage}`,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
      errorType: 'validation',
      severity: 'low',
      metadata: {
        field,
        value: this.sanitizeValue(field, value) as string | number | boolean | null,
        ...metadata
      }
    };

    // Add user info if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      errorLog.userId = currentUser.id;
      errorLog.userEmail = currentUser.email;
    }

    await this.saveError(errorLog);
  }

  /**
   * Log an authentication error
   */
  async logAuthError(
    errorCode: string,
    errorMessage: string,
    metadata?: Metadata
  ): Promise<void> {
    const errorLog: ErrorLog = {
      message: `Auth error (${errorCode}): ${errorMessage}`,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
      errorType: 'authentication',
      severity: 'high',
      metadata: {
        errorCode,
        ...metadata
      }
    };

    await this.saveError(errorLog);
  }

  /**
   * Log a permission error
   */
  async logPermissionError(
    action: string,
    resource: string,
    metadata?: Metadata
  ): Promise<void> {
    const errorLog: ErrorLog = {
      message: `Permission denied: ${action} on ${resource}`,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date(),
      errorType: 'permission',
      severity: 'high',
      metadata: {
        action,
        resource,
        ...metadata
      }
    };

    // Add user info if available
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      errorLog.userId = currentUser.id;
      errorLog.userEmail = currentUser.email;
    }

    await this.saveError(errorLog);
  }

  /**
   * Save error to Firestore, with a console fallback that also works in
   * production. Never throws - logging must not become its own failure mode.
   */
  private async saveError(errorLog: ErrorLog): Promise<void> {
    try {
      if (IS_DEVELOPMENT) {
        console.error('Error logged:', errorLog);
      }

      if (!this.isOnline) {
        // Queue errors when offline, oldest-first eviction so a long offline
        // session cannot grow the queue without bound.
        this.errorQueue.push(errorLog);
        if (this.errorQueue.length > MAX_QUEUED_ERRORS) {
          this.errorQueue.splice(0, this.errorQueue.length - MAX_QUEUED_ERRORS);
        }
        return;
      }

      if (!ERROR_LOGGING_ENABLED) {
        if (!IS_DEVELOPMENT) {
          console.error('Error (remote logging disabled):', errorLog);
        }
        return;
      }

      await addDoc(collection(db, 'errorLogs'), {
        ...errorLog,
        timestamp: serverTimestamp()
      });
    } catch (error) {
      // Fallback to console if Firestore fails - in every environment, so a
      // rules/network failure does not make the error disappear entirely.
      console.error('Failed to log error to Firestore:', error);
      console.error('Original error:', errorLog);
    }
  }

  /**
   * Flush error queue when coming back online
   */
  private async flushErrorQueue(): Promise<void> {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    for (const error of errors) {
      await this.saveError(error);
    }
  }

  /**
   * Get current user info from Firebase Auth.
   * Synchronous and context-free on purpose - the error pipeline must not
   * depend on React state that may not be mounted when an error fires.
   */
  private getCurrentUser(): { id: string; email: string } | null {
    const user = auth.currentUser;
    if (!user) return null;
    return { id: user.uid, email: user.email || '' };
  }

  /**
   * Redact values whose FIELD NAME marks them sensitive.
   *
   * The previous implementation tested the VALUE for the substring 'password',
   * so it redacted the literal string "password" and logged an actual password
   * verbatim.
   */
  private sanitizeValue(field: string, value: unknown): unknown {
    if (SENSITIVE_FIELD_PATTERN.test(field)) {
      return '[REDACTED]';
    }
    // Never log object/array payloads wholesale - they may carry nested secrets.
    if (value !== null && typeof value === 'object') {
      return '[OBJECT]';
    }
    return value;
  }

  /**
   * Create user-friendly error messages
   */
  static getUserFriendlyMessage(error: unknown): string {
    const asRecord = (err: unknown): { code?: unknown; message?: unknown } =>
      (typeof err === 'object' && err !== null ? err : {}) as { code?: unknown; message?: unknown };

    const record = asRecord(error);
    const code = typeof record.code === 'string' ? record.code : undefined;
    const message = typeof record.message === 'string' ? record.message : '';

    // Network errors. Checked FIRST and independently of `code`: a plain
    // Error('network timeout') carries no code at all, and the old nesting made
    // the message branch unreachable for exactly that case.
    if (
      code === 'network-request-failed' ||
      code === 'auth/network-request-failed' ||
      code === 'functions/unavailable' ||
      code === 'unavailable' ||
      /network|offline|failed to fetch|err_internet/i.test(message)
    ) {
      return 'Network connection issue. Please check your internet connection.';
    }

    // Firebase Auth errors
    if (code?.startsWith('auth/')) {
      switch (code) {
        case 'auth/user-not-found':
          return 'No account found with this email address.';
        case 'auth/wrong-password':
          return 'Incorrect password. Please try again.';
        case 'auth/email-already-in-use':
          return 'This email is already registered.';
        case 'auth/weak-password':
          return 'Password is too weak. Please use a stronger password.';
        case 'auth/invalid-email':
          return 'Invalid email address format.';
        case 'auth/too-many-requests':
          return 'Too many failed attempts. Please try again later.';
        default:
          return 'Authentication error. Please try again.';
      }
    }

    // Callable function errors. HttpsError messages are written to be shown to
    // the caller, so surface the server's text where it is meaningful.
    if (code?.startsWith('functions/')) {
      switch (code) {
        case 'functions/unauthenticated':
          return 'Your session has expired. Please log in again.';
        case 'functions/permission-denied':
          return message || 'You do not have permission to perform this action.';
        case 'functions/not-found':
          return message || 'The requested resource was not found.';
        case 'functions/invalid-argument':
        case 'functions/failed-precondition':
        case 'functions/already-exists':
        case 'functions/resource-exhausted':
          return message || 'That request could not be completed.';
        default:
          return message || 'The server could not complete that request. Please try again.';
      }
    }

    // Firestore errors
    if (code === 'permission-denied') {
      return 'You do not have permission to perform this action.';
    }

    if (code === 'not-found') {
      return 'The requested resource was not found.';
    }

    // Generic errors - don't expose technical details to users
    if (message) {
      if (IS_DEVELOPMENT) {
        return message;
      }
      return 'An unexpected error occurred. Please try again.';
    }

    return 'Something went wrong. Please try again later.';
  }
}

export const errorService = ErrorService.getInstance();

/**
 * Re-entrancy guard. logError is async, so a rejection raised inside it (a
 * denied Firestore write, say) surfaces as a NEW unhandledrejection, which
 * re-enters this handler and amplifies. The flag plus the .catch() below break
 * that loop.
 */
let handlingGlobalError = false;

const reportGlobal = (error: Error, metadata?: Metadata, componentStack?: string): void => {
  if (handlingGlobalError) return;
  handlingGlobalError = true;

  errorService
    .logError(error, componentStack ? { componentStack } : undefined, 'high', metadata)
    .catch((loggingError) => {
      // Swallow: rethrowing here would land straight back in this handler.
      console.error('errorService: failed to record global error:', loggingError);
    })
    .finally(() => {
      handlingGlobalError = false;
    });
};

// Global error handler
window.addEventListener('error', (event) => {
  reportGlobal(
    new Error(event.message),
    undefined,
    `at ${event.filename}:${event.lineno}:${event.colno}`
  );
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  reportGlobal(
    new Error(`Unhandled Promise Rejection: ${event.reason}`),
    { promise: String(event.promise) }
  );
});

export { ErrorService };
export default errorService;